import React, { PropsWithChildren } from 'react'

import { Root } from '@radix-ui/react-radio-group'
import { css, styled } from 'styled-components'

import { Field } from 'core/hooks/useForm'
import { outline, surfaceBright } from 'core/styles/tokens'

type Variant = 'default' | 'default-with-border' | 'grid-with-border' | 'compact'

type RadioGroupProps<T extends string> = Field<T> &
  PropsWithChildren<{
    className?: string
    ariaLabel?: string
    /** When true, only the child whose value matches current value is rendered (e.g. read-only display). */
    displaySelectedOnly?: boolean
    variant?: Variant
    /** When variant is `grid-with-border`, number of columns (e.g. 2 = two across). */
    columns?: number
  }>

/** A column of `RadioItem`s. */
const RadioGroup = <T extends string>({
  className,
  ariaLabel,
  children,
  value,
  onChange,
  disabled,
  name,
  displaySelectedOnly = false,
  variant = 'default',
  columns = 2,
}: RadioGroupProps<T>) => (
  <StyledRoot
    $columns={variant === 'grid-with-border' ? columns : undefined}
    $variant={variant}
    aria-label={ariaLabel}
    className={className}
    disabled={disabled || displaySelectedOnly}
    name={name}
    onValueChange={onChange}
    value={value}
  >
    {/* Only display the selected option when displaySelectedOnly is true */}
    {displaySelectedOnly && value != null ?
      React.Children.toArray(children).filter(
        (child) => React.isValidElement(child) && (child.props as { value?: string }).value === value,
      )
    : children}
  </StyledRoot>
)

export default RadioGroup

const withBorder = css`
  box-sizing: border-box;
  border: 1px solid ${outline};
  border-radius: 8px;
  background: ${surfaceBright};
  padding: 6px 12px;
`

const StyledRoot = styled(Root)<{
  $variant?: Variant
  $columns?: number
}>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 12px;

  ${(p) =>
    p.$variant === 'default-with-border' &&
    css`
      ${withBorder};
    `}

  ${(p) =>
    p.$variant === 'grid-with-border' &&
    css`
      ${withBorder};
      display: grid;
      flex-direction: row;
      grid-template-columns: repeat(${p.$columns}, 1fr);
      width: 100%;

      & > * {
        min-width: 0;
        ${p.$columns == null ? 'flex: 1;' : ''}
      }
    `}

  ${(p) =>
    p.$variant === 'compact' &&
    css`
      ${withBorder};
      align-self: flex-start;
      width: fit-content;
    `}
`
